"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Loader2, Copy, Check, Mail, AlertTriangle } from "lucide-react"
import { useAuth } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"
import { ROLE_HIERARCHY } from "@/lib/auth/permissions.utils"
import type { Role } from "@/lib/auth/permissions.types"

interface SendingEmailInfo {
  email: string | null
  isDefault: boolean
  configured: boolean
  isPlatformFallback: boolean
}

interface InvitedUser {
  email: string
  name: string
  role: string
}

interface InviteUserFormProps {
  onSuccess?: () => void
}

export function InviteUserForm({ onSuccess }: InviteUserFormProps = {}) {
  const { fetchWithAuth, user } = useAuth()
  const { isSuperUser } = usePermissions()
  const currentUserLevel = user?.role ? (ROLE_HIERARCHY[user.role as Role] ?? 99) : 99
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<{ user: InvitedUser; inviteUrl: string; emailSent: boolean; sendingEmail: string | null } | null>(null)
  const [copied, setCopied] = useState(false)
  const [sendingEmailInfo, setSendingEmailInfo] = useState<SendingEmailInfo | null>(null)
  const [loadingSendingEmail, setLoadingSendingEmail] = useState(true)

  useEffect(() => {
    async function fetchSendingEmail() {
      try {
        const response = await fetchWithAuth("/api/auth/google/sending-email")
        if (response.ok) {
          const data = await response.json()
          setSendingEmailInfo(data)
        }
      } catch (err) {
        console.error("Failed to fetch sending email:", err)
      } finally {
        setLoadingSendingEmail(false)
      }
    }
    fetchSendingEmail()
  }, [fetchWithAuth])

  const [formData, setFormData] = useState({
    email: "",
    name: "",
    role: "viewer" as Role,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await fetchWithAuth("/api/users/invite", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || "Failed to invite user")
      }

      setSuccess({
        user: data.user,
        inviteUrl: data.invite_url,
        emailSent: data.email_sent,
        sendingEmail: data.sending_email,
      })

      // Reset form
      setFormData({
        email: "",
        name: "",
        role: "viewer",
      })

      // Don't auto-redirect, let user see the invite URL and decide when to close
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to invite user")
    } finally {
      setLoading(false)
    }
  }

  const copyInviteUrl = () => {
    if (success?.inviteUrl) {
      navigator.clipboard.writeText(success.inviteUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite New User</CardTitle>
        <CardDescription>Send an invitation to join your organization</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              disabled={loading}
            />
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              type="text"
              placeholder="John Doe"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              disabled={loading}
            />
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select
              value={formData.role}
              onValueChange={(value) => setFormData({ ...formData, role: value as Role })}
              disabled={loading}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {isSuperUser && <SelectItem value="super_user">Super User</SelectItem>}
                {/* integrator is platform-staff: only super_user can assign it (D1) */}
                {isSuperUser && <SelectItem value="integrator">Integrator</SelectItem>}
                {currentUserLevel <= ROLE_HIERARCHY.admin && <SelectItem value="admin">Admin</SelectItem>}
                {currentUserLevel <= ROLE_HIERARCHY.manager && <SelectItem value="manager">Manager</SelectItem>}
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sending Email Info */}
          <div className="rounded-md border p-3 bg-muted/50">
            <div className="flex items-start gap-2">
              {loadingSendingEmail ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading sending email...</span>
                </div>
              ) : sendingEmailInfo?.configured ? (
                <>
                  <Mail className="h-4 w-4 text-green-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-muted-foreground">
                      <span>Invitation will be sent from:</span>{" "}
                      <span className="font-medium text-foreground">{sendingEmailInfo.email}</span>
                      {sendingEmailInfo.isPlatformFallback ? (
                        <span className="ml-1 text-xs text-yellow-600">(platform default)</span>
                      ) : (
                        sendingEmailInfo.isDefault && (
                          <span className="ml-1 text-xs text-yellow-600">(default)</span>
                        )
                      )}
                    </p>
                    {sendingEmailInfo.isPlatformFallback && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Connect your own email in Integrations → Google to send invitations from your
                        address.
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-yellow-700">
                      No sending email configured. The invitation link will need to be shared manually.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Configure a default email in Integrations → Google to enable automatic email sending.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert>
              <AlertDescription>
                <div className="space-y-3">
                  <p className="font-semibold">✅ User invited successfully!</p>
                  <div className="space-y-1">
                    <p>
                      <strong>Email:</strong> {success.user.email}
                    </p>
                    <p>
                      <strong>Name:</strong> {success.user.name}
                    </p>
                    <p>
                      <strong>Role:</strong> {success.user.role}
                    </p>
                  </div>

                  {/* Email sending status */}
                  {success.emailSent ? (
                    <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-2 rounded">
                      <Mail className="h-4 w-4" />
                      <span>
                        Invitation email sent from <strong>{success.sendingEmail}</strong>
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 text-sm text-yellow-700 bg-yellow-50 p-2 rounded">
                      <AlertTriangle className="h-4 w-4 mt-0.5" />
                      <span>
                        Email was not sent automatically. Please share the invitation link below with the user.
                      </span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Invitation Link:</Label>
                    <div className="flex gap-2">
                      <Input
                        value={success.inviteUrl}
                        readOnly
                        className="font-mono text-xs"
                        onClick={(e) => e.currentTarget.select()}
                      />
                      <Button type="button" variant="outline" size="sm" onClick={copyInviteUrl}>
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      ⚠️ Share this link securely with the user. It will expire in 7 days.
                    </p>
                  </div>
                  {/* Action buttons after success */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSuccess(null)
                        setError(null)
                      }}
                      className="flex-1"
                    >
                      Invite Another User
                    </Button>
                    {onSuccess && (
                      <Button type="button" onClick={onSuccess} className="flex-1">
                        Back to User Management
                      </Button>
                    )}
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Submit Button - hide when success is shown */}
          {!success && (
            <Button type="submit" disabled={loading} className="w-full">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <span>Send Invitation</span>
            </Button>
          )}
        </form>
      </CardContent>
    </Card>
  )
}
